<template>
  <div class="fixed bottom-20 right-4 z-50">
    <!-- Resizable chat panel -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 scale-95 translate-y-4"
      enter-to-class="opacity-100 scale-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 scale-100 translate-y-0"
      leave-to-class="opacity-0 scale-95 translate-y-4"
    >
      <VueDraggableResizable
        v-if="chatStore.isOpen"
        class-name="chat-container mb-4 bg-white dark:bg-gray-800 rounded-xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700"
        :w="chatWidth"
        :h="chatHeight"
        :min-width="MIN_WIDTH"
        :min-height="MIN_HEIGHT"
        :max-width="maxWidth"
        :max-height="maxHeight"
        :draggable="false"
        :resizable="true"
        :handles="['tl']"
        :parent="false"
        @resizing="onResizing"
        @resize-stop="onResizeStop"
      >
        <ChatHeader
          :selected-tenant-name="chatStore.selectedTenantName"
          :can-switch-tenant="shouldDropdownVisible"
          :is-tenant-selector-open="isTenantSelectorOpen"
          @close="handleClose"
          @clear="handleClear"
          @toggle-tenant-selector="handleToggleTenantSelector"
        />

        <div ref="messagesContainer" class="flex-1 overflow-y-auto p-4 min-h-0" @scroll="handleScroll">
          <ChatMessageComponent v-for="msg in chatStore.messages" :key="msg.id" :message="msg" />

          <div v-if="chatStore.error" class="mt-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <p class="text-xs text-red-600 dark:text-red-400">{{ chatStore.error }}</p>
          </div>
        </div>

        <!-- Terms of Service Notice -->
        <div class="px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <p class="text-[10px] text-gray-600 dark:text-gray-400 text-center">
            By using this chat service, you agree to our
            <button
              type="button"
              class="text-indigo-600 dark:text-indigo-400 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded px-0.5"
              @click="showTermsDialog = true"
            >
              Terms of Service
            </button>
          </p>
        </div>

        <ChatInput
          ref="chatInputRef"
          :disabled="chatStore.isStreaming"
          :is-loading="chatStore.isStreaming"
          placeholder="Type a message..."
          @send="handleSend"
        />

        <!-- Tenant Selector (inline overlay within chat widget) -->
        <ChatTenantSelector
          :tenants="tenantDropdownOptions"
          :selected-tenant-id="chatStore.selectedTenantId"
          :is-open="isTenantSelectorOpen"
          @select="handleTenantSelect"
          @close="isTenantSelectorOpen = false"
        />

        <ChatTermsDialog :is-open="showTermsDialog" @close="showTermsDialog = false" />
      </VueDraggableResizable>
    </Transition>
    <!-- Side-attached trigger tab - positioned above scroll-to-top button -->
    <button
      v-if="!chatStore.isOpen"
      class="fixed right-0 bottom-20 flex flex-col items-center justify-center gap-1 px-1.5 py-4 rounded-l-lg shadow-lg transition-all duration-200 hover:pr-2.5 bg-gradient-to-b from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
      @click="handleOpen()"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-5 w-5 text-white"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
        />
      </svg>
      <span class="text-white text-xs font-medium writing-vertical">Chat</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, watch, computed, onMounted, onUnmounted } from 'vue';
import { TrackingEventType } from '@/services/tracking.service';
import { useTracking } from '@/composables/use-tracking';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import VueDraggableResizable from 'vue-draggable-resizable';
import 'vue-draggable-resizable/style.css';
import { useChatStore } from '@/stores/chat.store';
import { streamChat, type ChatMessage as ServiceChatMessage } from '@/services/chat.service';
import { sanitizeError } from '@/utils/error.utils';
import { useImpersonationInfo } from '@/hooks/useImpersonationInfo';
import useMultitenancy from '@/hooks/useMultitenancy';
import { usePartnerTenantStore } from '@/stores/partnerTenant.storage';
import { useImpersonationStore } from '@/stores/impersonation.store';
import type { TenantOption } from '@/components/tenancy-dropdown/type';
import ChatHeader from './ChatHeader.vue';
import ChatMessageComponent from './ChatMessage.vue';
import ChatInput from './ChatInput.vue';
import ChatTermsDialog from './ChatTermsDialog.vue';
import ChatTenantSelector from './ChatTenantSelector.vue';

const chatStore = useChatStore();
const { trackComponentInteraction } = useTracking();
const showTermsDialog = ref(false);
const { impersonatedUser } = useImpersonationInfo();
const partnerTenantStore = usePartnerTenantStore();
const impersonationStore = useImpersonationStore();

// Leverage useMultitenancy hook for tenant fetching, permissions, and options
const { tenantDropdownOptions, shouldDropdownVisible, fetchRelatedTenants } = useMultitenancy({ shouldRun: true });

const isTenantSelectorOpen = ref(false);

const CHAT_SIZE_KEY = 'chat-widget-size';
const DEFAULT_WIDTH = 384;
const DEFAULT_HEIGHT = 500;
const MIN_WIDTH = 320;
const MIN_HEIGHT = 300;
const BASE_MAX_WIDTH = 1600;
const BASE_MAX_HEIGHT = 800;
const VIEWPORT_MARGIN = 48;

const chatWidth = ref(DEFAULT_WIDTH);
const chatHeight = ref(DEFAULT_HEIGHT);
const viewportWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024);
const viewportHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 768);

const maxWidth = computed(() => {
  const viewportMax = viewportWidth.value - VIEWPORT_MARGIN;
  return Math.min(BASE_MAX_WIDTH, viewportMax);
});

const maxHeight = computed(() => {
  const viewportMax = viewportHeight.value - VIEWPORT_MARGIN - 80;
  return Math.min(BASE_MAX_HEIGHT, viewportMax);
});

const clampDimensions = (): void => {
  chatWidth.value = Math.min(Math.max(chatWidth.value, MIN_WIDTH), maxWidth.value);
  chatHeight.value = Math.min(Math.max(chatHeight.value, MIN_HEIGHT), maxHeight.value);
};

const updateViewportDimensions = (): void => {
  viewportWidth.value = window.innerWidth;
  viewportHeight.value = window.innerHeight;
  clampDimensions();
};

const throttledUpdate = throttle(updateViewportDimensions, 150);
const debouncedUpdate = debounce(updateViewportDimensions, 100);

const handleViewportResize = (): void => {
  throttledUpdate();
  debouncedUpdate();
};

const loadSize = (): void => {
  try {
    const stored = sessionStorage.getItem(CHAT_SIZE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as { width?: number; height?: number };
      if (typeof parsed.width === 'number') {
        chatWidth.value = parsed.width;
      }
      if (typeof parsed.height === 'number') {
        chatHeight.value = parsed.height;
      }
    }
  } catch {
    // Ignore storage errors
  }
  clampDimensions();
};

const saveSize = (): void => {
  try {
    sessionStorage.setItem(
      CHAT_SIZE_KEY,
      JSON.stringify({
        width: chatWidth.value,
        height: chatHeight.value,
      }),
    );
  } catch {
    // Ignore storage errors
  }
};

const onResizing = (_x: number, _y: number, width: number, height: number): void => {
  chatWidth.value = width;
  chatHeight.value = height;
};

const onResizeStop = (_x: number, _y: number, width: number, height: number): void => {
  chatWidth.value = width;
  chatHeight.value = height;
  saveSize();
};

onMounted(() => {
  loadSize();
  window.addEventListener('resize', handleViewportResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleViewportResize);
});

const toServiceMessages = (messages: typeof chatStore.messages): ServiceChatMessage[] => {
  return messages
    .filter((msg) => msg.content.trim().length > 0)
    .map((msg) => ({
      role: msg.role,
      content: msg.content,
    }));
};
const messagesContainer = ref<HTMLElement | null>(null);
const chatInputRef = ref<InstanceType<typeof ChatInput> | null>(null);
const userHasScrolledUp = ref(false);

const isNearBottom = (): boolean => {
  if (!messagesContainer.value) return true;
  const { scrollTop, scrollHeight, clientHeight } = messagesContainer.value;
  const threshold = 50;
  return scrollHeight - scrollTop - clientHeight < threshold;
};

const handleScroll = (): void => {
  userHasScrolledUp.value = !isNearBottom();
};

const scrollToBottom = (force = false): void => {
  if (!force && userHasScrolledUp.value) return;

  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
      userHasScrolledUp.value = false;
    }
  });
};

watch(
  () => chatStore.messages.length,
  () => {
    scrollToBottom();
  },
);

// Identity the chat acts as: the impersonated (or signed-in) user plus the
// partner-managed tenant. Read from the stores so changes are reactive.
const chatContextKey = computed(() => {
  const userId = impersonationStore.isImpersonating ? impersonationStore.impersonatedUser?.id : impersonatedUser?.id;
  return `${userId ?? ''}:${partnerTenantStore.tenant?.id ?? ''}`;
});

// Start a fresh chat whenever the identity changes. Immediate so a remount
// (e.g. after stopping and restarting impersonation) is also caught.
watch(
  chatContextKey,
  (contextKey) => {
    chatStore.syncContext(contextKey);
    isTenantSelectorOpen.value = false;
    if (chatStore.isOpen) {
      fetchRelatedTenants();
    }
  },
  { immediate: true },
);

// Sync chat tenant when useMultitenancy options change
watch(
  () => tenantDropdownOptions.value,
  (options) => {
    if (!options.length) return;

    // Validate persisted tenant or set default
    if (chatStore.selectedTenantId) {
      const isValid = options.some((t: TenantOption) => t.id === chatStore.selectedTenantId);
      if (!isValid) {
        chatStore.setTenant(options[0].id, options[0].name);
      }
    } else {
      chatStore.setTenant(options[0].id, options[0].name);
    }
  },
  { immediate: true },
);

watch(
  () => chatStore.isOpen,
  (isOpen) => {
    if (isOpen) {
      // Refetch tenants on each chat open via useMultitenancy hook
      fetchRelatedTenants();
      isTenantSelectorOpen.value = false;

      nextTick(() => {
        chatInputRef.value?.focus();
        scrollToBottom(true);
      });
    }
  },
);

const handleOpen = (): void => {
  chatStore.open();
  trackComponentInteraction(TrackingEventType.CHAT_OPENED, 'chat_widget');
};

const handleSend = async (message: string): Promise<void> => {
  chatStore.setError(null);

  trackComponentInteraction(
    TrackingEventType.CHAT_MESSAGE_SENT,
    'chat_widget',
    chatStore.selectedTenantId ? { selected_tenant_id: chatStore.selectedTenantId } : undefined,
  );

  // Validate tenant access before sending
  if (chatStore.selectedTenantId && shouldDropdownVisible.value) {
    const hasAccess = tenantDropdownOptions.value.some((t: TenantOption) => t.id === chatStore.selectedTenantId);
    if (!hasAccess) {
      chatStore.setError('Selected tenant is no longer accessible. Please select a valid tenant.');
      return;
    }
  }

  const historyMessages = toServiceMessages(chatStore.messages);

  chatStore.addUserMessage(message);
  chatStore.setIsStreaming(true);
  chatStore.addAssistantMessage('', true);

  scrollToBottom(true);

  try {
    for await (const chunk of streamChat(
      {
        message,
        messages: historyMessages.length > 0 ? historyMessages : undefined,
        tenantIds: chatStore.selectedTenantId ? [chatStore.selectedTenantId] : undefined,
        managedTenantId: partnerTenantStore.tenant?.id,
      },
      chatStore.getChatId(),
    )) {
      if (chunk.error) {
        chatStore.setError(sanitizeError(chunk.error));
        break;
      }
      if (chunk.data) {
        chatStore.appendToLastAssistantMessage(chunk.data);
        scrollToBottom();
      }
      if (chunk.metadata?.stop_reason === 'max_tokens') {
        chatStore.appendToLastAssistantMessage(
          '\n\n---\n\n*⚠️ Response reached length limit. Feel free to ask me to continue or rephrase your question for a more focused answer.*',
        );
        scrollToBottom();
      }
    }
  } catch (error) {
    chatStore.setError(sanitizeError(error));
  } finally {
    const lastMessage = chatStore.messages[chatStore.messages.length - 1];
    if (lastMessage?.role === 'assistant' && !lastMessage.content.trim()) {
      chatStore.messages.pop();
    }
    chatStore.finishStreaming();
    nextTick(() => {
      chatInputRef.value?.focus();
    });
  }
};

const handleClear = (): void => {
  chatStore.clearHistory();
};

const handleClose = (): void => {
  chatStore.close();
  isTenantSelectorOpen.value = false;
};

const handleToggleTenantSelector = (): void => {
  isTenantSelectorOpen.value = !isTenantSelectorOpen.value;
};

const handleTenantSelect = (tenant: TenantOption): void => {
  isTenantSelectorOpen.value = false;
  if (tenant.id !== chatStore.selectedTenantId) {
    chatStore.switchTenant(tenant.id, tenant.name);
  }
};
</script>

<style scoped>
.writing-vertical {
  writing-mode: vertical-rl;
  text-orientation: mixed;
}

.chat-container {
  position: relative !important;
  transform: none !important;
  top: auto !important;
  left: auto !important;
  display: flex !important;
  flex-direction: column !important;
  border: none !important;
}

:deep(.handle) {
  border: none !important;
  background: transparent !important;
}

:deep(.handle-tl) {
  width: 44px !important;
  height: 44px !important;
  top: 0 !important;
  left: 0 !important;
  cursor: nwse-resize !important;
  border: none !important;
  background: transparent !important;
  border-top-left-radius: 12px;
  transition: opacity 0.15s ease;
  touch-action: none;
  z-index: 30 !important;
}

:deep(.handle-tl:hover),
:deep(.handle-tl:active) {
  opacity: 0.8;
}

:deep(.handle-tl::before) {
  content: '';
  position: absolute;
  top: 8px;
  left: 8px;
  width: 20px;
  height: 20px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cline x1='6' y1='6' x2='18' y2='18'/%3E%3Cpolyline points='6 10 6 6 10 6'/%3E%3Cpolyline points='18 14 18 18 14 18'/%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  opacity: 0.25;
}
</style>
